import { ReactNode } from 'react';
import { GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import {
  PageViewTracker,
  INTERCOM_CONFIG,
  schemaData,
  COOKIES_CONFIG,
} from '@belong/common';
import {
  Text,
  TestimonialsCarousel,
  BulletList,
  LinkBase,
  BelongExplainerVideo,
  Image,
  Link,
} from '@belong/ui';
import { Footer } from 'src/components/footer/footer';
import clsx from 'clsx';
import { kebabCase } from 'es-toolkit/compat';
import { parseCookies } from 'nookies';
import { fetchCityListService } from 'src/api/geo';
import { fetchRegionsService } from 'src/api/region';
import { Header } from 'src/components/header/header';
import { IntercomWidgetProps } from 'src/components/intercom-widget/intercom-widget';
import { MetaSEO } from 'src/components/meta-seo/meta-seo';
import { RatingRotator } from 'src/components/rating-rotator/rating-rotator';
import { FaqSection } from 'src/features/home-page/components/FaqSection/FaqSection';
import { HomeAnimations } from 'src/features/home-page/components/HomeAnimations/HomeAnimations';
import { ReferralCard } from 'src/features/home-page/components/ReferralCard/ReferralCard';
import { Hero } from 'src/features/home-page/components/hero/hero';
import { SocialProof } from 'src/features/home-page/components/social-proof/social-proof';
import { FEATURED_CITIES } from 'src/features/home-page/constants';
import { HOME_PAGE_STRINGS } from 'src/features/home-page/home-page.strings';
import styles from 'src/features/home-page/home.module.css';

const IntercomWidget = dynamic<IntercomWidgetProps>(
  () =>
    import('src/components/intercom-widget/intercom-widget').then(
      (mod) => mod.IntercomWidget,
    ),
  { ssr: false },
);

const metaDescription = HOME_PAGE_STRINGS['meta.description'] as string;
const metaTitle = HOME_PAGE_STRINGS['meta.title'] as string;

type Props = {
  featuredCitiesAndRegions: {
    href: string;
    displayName: string;
  }[];
};

export default function IndexPage({ featuredCitiesAndRegions }: Props) {
  console.log('test env variable', process.env.NEXT_PUBLIC_ENV);
  return (
    <PageViewTracker name="home_page">
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
        />
      </Head>
      <MetaSEO
        title={metaTitle}
        description={metaDescription}
        openGraph={{
          description: metaDescription,
          type: 'video.other',
          url: 'https://s3-us-west-2.amazonaws.com/belong-web-assets/Belong.mp4',
        }}
      />
      <IntercomWidget isEnabled={INTERCOM_CONFIG.homeowner.isEnabled} />
      <div className="bg-dark-navy w-screen ">
        <div
          className={clsx(
            styles.grid,
            'bg-dark-navy lg:pb-3xl relative mx-auto max-w-[1890px] overflow-hidden',
          )}
        >
          <div className={clsx(styles.gradient)} />
          <HomeAnimations />
          <div className="relative z-10">
            <section className="mb-2xl xl:mb-3xl">
              <Hero />
            </section>
            <section className="mb-3xl md:mb-5xl flex items-center justify-center">
              <SocialProof />
            </section>
          </div>
        </div>
      </div>
      <section className="mb-3xl md:mb-5xl w-full" id="presentation-video">
        <BelongExplainerVideo
          autoplay
          wrapperClassName={styles.videoContainer}
          playIconStyle="green"
          videoClassName="border-none"
          buttonWrapperClassName="block"
        />
      </section>
      <section className="mb-3xl md:mb-5xl container flex flex-col items-center justify-center text-center">
        <div
          className={clsx(
            'mb-xs md:mb-sm max-w-xs md:max-w-none',
            styles.featureTitle,
          )}
        >
          <Text as="h2" variant="h2" fontWeight="semibold">
            {HOME_PAGE_STRINGS.featuresTitle}
          </Text>
        </div>
        <div
          className={clsx(
            'mb-2xl w-full max-w-sm sm:max-w-md md:max-w-none',
            styles.featureDescription,
          )}
        >
          <Text>{HOME_PAGE_STRINGS.featuresDescription}</Text>
        </div>
        <div className="mb-2xl w-full sm:hidden">
          <video
            width="100%"
            height="100%"
            autoPlay
            controls={false}
            muted={true}
            loop
            playsInline
            className="rounded"
          >
            <source
              src="https://belong-consumer-assets.s3.us-west-1.amazonaws.com/home-page/home-chat-3x.mp4"
              type="video/mp4"
            />
            Sorry, your browser does not support embedded videos.
          </video>
        </div>
        <div className="mb-3xl md:mb-5xl w-full">
          <div className="contents sm:hidden">
            <video
              width="100%"
              height="100%"
              autoPlay
              controls={false}
              muted={true}
              loop
              playsInline
              className="rounded"
            >
              <source
                src="https://belong-consumer-assets.s3.us-west-1.amazonaws.com/home-page/home-chat-mobile-3x.mp4"
                type="video/mp4"
              />
              Sorry, your browser does not support embedded videos.
            </video>
          </div>
          <div className="hidden sm:contents">
            <video
              width="100%"
              height="100%"
              autoPlay
              controls={false}
              muted={true}
              loop
              playsInline
              className="rounded"
            >
              <source
                src="https://belong-consumer-assets.s3.us-west-1.amazonaws.com/home-page/pro-service-3x.mp4"
                type="video/mp4"
              />
              Sorry, your browser does not support embedded videos.
            </video>
          </div>
        </div>
        <div className="gap-xl xl:gap-lg flex w-full flex-col lg:flex-row">
          <div
            className="border-gray relative flex flex-col overflow-hidden rounded border border-solid lg:flex-1"
            style={{ backgroundColor: '#FCFFF5' }}
          >
            <div className={clsx(styles.ownRentalTitleWrapper)}>
              <div className={clsx(styles.ownRentalTitle)}>
                <Text variant="h3" fontWeight="semibold">
                  {HOME_PAGE_STRINGS.ownRentalHomeTitle}
                </Text>
              </div>
              <div className={clsx(styles.ownRentalDescription)}>
                <Text>{HOME_PAGE_STRINGS.ownRentalHomeDescription}</Text>
              </div>
            </div>
            <div className={clsx(styles.ownRentalButton)}>
              <Link href="/homeowners" variant="solid" size="large">
                {HOME_PAGE_STRINGS.ownRentalHomeButton}
              </Link>
            </div>
            <Image
              src="home-page/rent-payments.png"
              alt=""
              sizes="(max-width: 768px) 400px, 500px"
              className={clsx(styles.ownRentalImage)}
            />
          </div>
          <div
            className="border-gray relative flex flex-col overflow-hidden rounded border border-solid lg:flex-1"
            style={{ backgroundColor: '#FCFFF5' }}
          >
            <div className={clsx(styles.rentingAdvantagesContainer)}>
              <div className={clsx(styles.rentingAdvantagesTitle)}>
                <Text variant="h3" fontWeight="semibold">
                  {HOME_PAGE_STRINGS.rentingAdvantagesTitle}
                </Text>
              </div>
              <div className={clsx(styles.rentingAdvantagesDescription)}>
                <Text>{HOME_PAGE_STRINGS.rentingAdvantagesDescription}</Text>
              </div>
            </div>
            <div className={clsx(styles.rentingAdvantagesButton)}>
              <Link href="/homes" variant="solid" size="large">
                {HOME_PAGE_STRINGS.rentingAdvantagesButton}
              </Link>
            </div>
            <Image
              src="home-page/team.png"
              alt=""
              sizes="(max-width: 768px) 400px, 500px"
              className={clsx(styles.rentingAdvantagesImage)}
            />
          </div>
        </div>
      </section>
      <section className="bg-darker-navy py-3xl lg:py-5xl">
        <div
          className="mb-xl md:mb-2xl container flex flex-col items-center text-white"
          id="testimonials"
        >
          <Text
            variant="h2"
            fontWeight="semibold"
            className={clsx(
              styles.testimonialsTitleMaxW,
              'mb-sm text-center md:max-w-none',
            )}
          >
            {HOME_PAGE_STRINGS.testimonialsTitle}
          </Text>
          <RatingRotator />
        </div>
        <div className="carousel-container flex justify-center">
          <TestimonialsCarousel
            theme="dark"
            blurredEnds={false}
            playIconStyle="green"
          />
        </div>
      </section>
      <div className="my-2xl lg:my-3xl container flex justify-center">
        <ReferralCard />
      </div>
      <div className="mb-xl container">
        <FaqSection />
      </div>
      <div className="mb-3xl text-dark-gray  lg:mb-4xl container">
        <Text
          variant="p1"
          fontWeight="semibold"
          className="mb-xs text-dark-gray"
        >
          {HOME_PAGE_STRINGS['featuredCities.title']}
        </Text>
        <BulletList
          wrap
          size="large"
          items={featuredCitiesAndRegions.map(({ href, displayName }) => {
            return (
              <LinkBase
                key={href}
                href={href}
                className="p1 leading-h2-sm hover:text-dark-navy"
              >
                {displayName}
              </LinkBase>
            );
          })}
        />
      </div>
    </PageViewTracker>
  );
}

export const getStaticProps: GetStaticProps<Props> = async () => {
  let featuredCitiesAndRegions: { href: string; displayName: string }[] = [];

  try {
    const [cities, regions] = await Promise.all([
      fetchCityListService(),
      fetchRegionsService(),
    ]);

    const formattedRegions = regions
      .filter(
        (region) =>
          FEATURED_CITIES.includes(region.name!) ||
          FEATURED_CITIES.includes(region.region!),
      )
      .map((region) => {
        const displayName =
          FEATURED_CITIES.find(
            (city) => city === region.name || city === region.region,
          ) ?? '';
        const href = `/homeowners/${kebabCase(region.name!)}`;

        return {
          href,
          displayName,
        };
      })
      .filter(Boolean);

    const formattedCities = (cities as { city: string; stateId: string }[])
      .filter((city) => FEATURED_CITIES.includes(city.city))
      .filter(
        (city) =>
          !formattedRegions.some((region) => region.displayName === city.city),
      )
      .map((city) => ({
        href: `/homeowners/${kebabCase(city.city)}-${city.stateId.toLowerCase()}-property-management`,
        displayName: `${city.city}`,
      }));

    featuredCitiesAndRegions = [...formattedRegions, ...formattedCities].sort(
      (a, b) => a.displayName.localeCompare(b.displayName),
    );
  } catch (_e) {
    return {
      props: {
        featuredCitiesAndRegions: [],
      },
    };
  }

  return {
    props: {
      featuredCitiesAndRegions,
    },
  };
};

IndexPage.getLayout = (page: ReactNode): ReactNode => {
  const cookies = parseCookies();

  const comeFromMobileApp = Boolean(
    cookies[COOKIES_CONFIG.MOBILE_APP.name] === 'true',
  );
  return (
    <div className="relative flex min-h-full flex-col overflow-x-hidden">
      {!comeFromMobileApp && (
        // z-20 keeps the header — and the mobile navigation sheet's close
        // button inside it — above the hero, which is also `relative z-10` and,
        // being later in the DOM, would otherwise paint over it (the hero's SEO
        // h1 bled through the open mobile sheet).
        <div className="relative z-20">
          <Header variant="tertiary" theme="dark" />
        </div>
      )}
      <main className="flex-1">{page}</main>
      <Footer />
    </div>
  );
};
